import React, { useCallback, useEffect, useState } from "react";
import { Modal, Spin, Tooltip, Input, Tag } from "antd";
import { useDispatch, useSelector } from "react-redux";

import * as resolutionsApi from "../../../../core/data/redux/services/resolutionsApi";
import { fetchResolutions } from "../../../../core/data/redux/projects/thunks";
import {
  selectActiveCompany,
  selectCurrentProject,
  selectResolutions,
} from "../../../../core/data/redux/projects/selectors";
import { useProjectCapability } from "../../hooks/useProjectRole";
import { errMsg } from "../../helpers/errMsg.helper";
import { CONFIRM_MODAL_PROPS } from "../../helpers/modal.style";
import { notify } from "../../../../services/toast.config";

import type { Resolution } from "../../../../core/data/redux/projects/types";
import type { AppDispatch } from "../../../../core/data/redux/store";

const ResolutionsSettings: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const project = useSelector(selectCurrentProject);
  const resolutions = useSelector(selectResolutions);
  const activeCompany = useSelector(selectActiveCompany);
  const canManage = useProjectCapability(project?.id, "MANAGE_RESOLUTION");

  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState<Resolution | null>(null);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(false);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      await dispatch(fetchResolutions(project?.id)).unwrap();
    } catch {
      /* silently handled */
    } finally {
      setLoading(false);
    }
  }, [dispatch, project?.id]);

  useEffect(() => { reload(); }, [reload]);

  const openCreate = () => {
    setEditing(null);
    setName("");
    setDescription("");
    setEditorOpen(true);
  };

  const openEdit = (r: Resolution) => {
    setEditing(r);
    setName(r.name);
    setDescription(r.description || "");
    setEditorOpen(true);
  };

  const handleSubmit = async () => {
    if (!name.trim()) return;
    setSubmitting(true);
    try {
      if (editing) {
        await resolutionsApi.updateResolution(activeCompany.company_id, activeCompany.company_type, editing.id, { name, description });
        notify.success("Resolution updated");
      } else {
        await resolutionsApi.createResolution(activeCompany.company_id, activeCompany.company_type, { name, description });
        notify.success("Resolution created");
      }
      await reload();
      setEditorOpen(false);
    } catch (e: any) {
      notify.error(errMsg(e, "Failed to save resolution"));
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = (r: Resolution) => {
    Modal.confirm({
      title: `Delete resolution '${r.name}'?`,
      okText: "Delete",
      okButtonProps: { danger: true },
      ...CONFIRM_MODAL_PROPS,
      onOk: async () => {
        try {
          await resolutionsApi.deleteResolution(activeCompany.company_id, activeCompany.company_type, r.id);
          await reload();
          notify.success("Resolution deleted");
        } catch (e: any) {
          notify.error(errMsg(e, "Failed to delete"));
        }
      },
    });
  };

  const handleSetDefault = async (r: Resolution) => {
    try {
      await resolutionsApi.setDefault(activeCompany.company_id, activeCompany.company_type, r.id);
      notify.success(`'${r.name}' set as default`);
      await reload();
    } catch (e: any) {
      notify.error(errMsg(e, "Failed to set default"));
    }
  };

  if (!project) {
    return (
      <div className="card border-0" style={{ borderRadius: 14, boxShadow: "0 1px 8px rgba(0,0,0,0.06)" }}>
        <div className="card-body d-flex justify-content-center align-items-center" style={{ minHeight: 200 }}>
          <div className="text-center">
            <div className="spinner-border text-primary" role="status" style={{ width: "2.5rem", height: "2.5rem" }} />
            <p className="mt-3 text-muted mb-0">Loading project…</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="card border-0" style={{ borderRadius: 14, boxShadow: "0 1px 8px rgba(0,0,0,0.06)" }}>
      <div className="card-header border-0 pb-0 d-flex align-items-center justify-content-between">
        <div>
          <h5 className="mb-1 d-flex align-items-center gap-2" style={{ whiteSpace: "nowrap" }}>
            <i className="ti ti-circle-check" style={{ color: "#3b82f6", fontSize: 20, flexShrink: 0 }} />
            <span>Resolutions</span>
          </h5>
          <span className="text-muted small">{resolutions.length} resolution{resolutions.length === 1 ? "" : "s"}</span>
        </div>
        {canManage && (
          <button type="button" className="btn btn-info btn-sm d-inline-flex align-items-center gap-1" style={{ borderRadius: 8, whiteSpace: "nowrap" }} onClick={openCreate}>
            <i className="ti ti-plus" style={{ fontSize: 16 }} />
            <span>New Resolution</span>
          </button>
        )}
      </div>
      <div className="card-body bg-white rounded-3">
        {loading && resolutions.length === 0 ? (
          <div className="text-center py-3"><Spin /></div>
        ) : resolutions.length === 0 ? (
          <div className="text-center py-4">
            <i className="ti ti-circle-check" style={{ fontSize: 40, color: "#cbd5e1" }} />
            <p className="text-muted mt-2 mb-0">No resolutions yet. Create resolutions like "Done", "Won't Fix", "Duplicate".</p>
          </div>
        ) : (
          <div className="d-flex flex-column gap-2">
            {resolutions.map((r) => (
              <div
                key={r.id}
                className="d-flex align-items-center gap-3 px-3 py-2"
                style={{ borderRadius: 10, border: "1px solid #e2e8f0", transition: "all 0.2s" }}
                onMouseEnter={(e) => { e.currentTarget.style.background = "#f8fafc"; e.currentTarget.style.borderColor = "#93c5fd"; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.borderColor = "#e2e8f0"; }}
              >
                <div
                  className="d-flex align-items-center justify-content-center"
                  style={{ width: 32, height: 32, borderRadius: 8, background: r.is_default ? "#ecfdf5" : "#f1f5f9", flexShrink: 0 }}
                >
                  <i className="ti ti-circle-check" style={{ color: r.is_default ? "#10b981" : "#64748b", fontSize: 16 }} />
                </div>
                <div className="flex-grow-1 min-width-0">
                  <div className="d-flex align-items-center gap-2 flex-wrap">
                    <span className="fw-semibold" style={{ fontSize: 13.5 }}>{r.name}</span>
                    {r.is_default && <Tag color="green" style={{ margin: 0, fontSize: 10, lineHeight: "18px", borderRadius: 10 }}>Default</Tag>}
                  </div>
                  {r.description && <div className="text-muted text-truncate" style={{ fontSize: 12 }}>{r.description}</div>}
                </div>
                {canManage && (
                  <div className="d-flex align-items-center gap-1" style={{ flexShrink: 0 }}>
                    {!r.is_default && (
                      <Tooltip title="Set as default">
                        <button title="Set as default" type="button" className="btn btn-sm btn-light d-flex align-items-center justify-content-center" style={{ borderRadius: 8, width: 32, height: 32 }} onClick={() => handleSetDefault(r)}>
                          <i className="ti ti-star" style={{ color: "#f59e0b", fontSize: 15 }} />
                        </button>
                      </Tooltip>
                    )}
                    <Tooltip title="Edit">
                      <button title="Edit" type="button" className="btn btn-sm btn-light d-flex align-items-center justify-content-center" style={{ borderRadius: 8, width: 32, height: 32 }} onClick={() => openEdit(r)}>
                        <i className="ti ti-edit" style={{ color: "#f59e0b", fontSize: 15 }} />
                      </button>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <button title="Delete" type="button" className="btn btn-sm btn-light d-flex align-items-center justify-content-center" style={{ borderRadius: 8, width: 32, height: 32 }} onClick={() => handleDelete(r)}>
                        <i className="ti ti-trash" style={{ color: "#ef4444", fontSize: 15 }} />
                      </button>
                    </Tooltip>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      <Modal
        title={editing ? "Edit Resolution" : "New Resolution"}
        open={editorOpen}
        onCancel={() => setEditorOpen(false)}
        onOk={handleSubmit}
        confirmLoading={submitting}
        okText={editing ? "Update" : "Create"}
        destroyOnClose
        maskClosable={false}
        keyboard={false}
        centered
        width="640px"
        zIndex={1001}
        styles={{
          content: { padding: 0, borderRadius: 12, overflow: "hidden" },
          header: { padding: "14px 20px 12px", borderBottom: "1px solid #f0f0f0", marginBottom: 0 },
          body: { padding: "16px 24px 20px" },
          footer: { padding: "10px 20px 14px", borderTop: "1px solid #f0f0f0", marginTop: 0 },
          mask: {
            position: "fixed",
            inset: 0,
            width: "100vw",
            height: "100vh",
            backdropFilter: "blur(6px)",
            WebkitBackdropFilter: "blur(6px)",
            background: "rgba(0,0,0,0.45)",
          },
        }}
      >
        <div className="mb-3">
          <label className="form-label small fw-semibold">Name</label>
          <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Fixed, Won't Fix, Duplicate" />
        </div>
        <div className="mb-3">
          <label className="form-label small fw-semibold">Description</label>
          <Input.TextArea rows={2} value={description} onChange={(e) => setDescription(e.target.value)} />
        </div>
      </Modal>
    </div>
  );
};

export default ResolutionsSettings;
